import React, { useRef } from "react";
import {
  Divider,
  FormControl,
  FormHelperText,
  TextField,
  Tooltip,
  styled,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import Typography from "@common/components/Typography";
import { TicketIssue } from "@services/tickets/types";
import { IssueName } from "@services/account/types";
import { useContext, useEffect, useState } from "react";
import { getIssue } from "@services/account";
import Select from "@common/components/Select";
import MenuItem from "@common/components/MenuItem";
import { Device, getProductName } from "@services/devices/types";
import { MeetingInstance } from "@services/meetings/types";
import SubTypePicker from "./SubTypePicker";
import { Priority } from "@services/common/types";
import { FormContext, TimeValue } from "../../context/formContext";
import { TicketListContext } from "@pages/Tickets/context";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { getSubject } from "../../utils";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const TimeValueLabels: { [k in TimeValue]: string } = {
  [TimeValue.FewSeconds]: "Few seconds ago",
  [TimeValue.NoClue]: "No clue",
  [TimeValue.EnterDate]: "Enter a date",
};

type TicketDetailsProps = {
  device?: Device;
  meeting?: MeetingInstance;
  devices?: Device[];
};

type IssueTypeState = {
  issueType: string;
  issueTypes: string[];
};

const StyledTypo = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.grey.g9B9B9B,
}));

const TicketDetails = (props: TicketDetailsProps) => {
  const { t } = useTranslation();
  const {
    dispatchTicket,
    dispatchDetails,
    dispatchTimeOfIssue,
    dispatchHasChanged,
    form,
    canEdit,
    timeOfIssue,
  } = useContext(FormContext);
  const { isNewTicket } = useContext(TicketListContext);
  const { ticket, details } = form;
  const { device, meeting, devices } = props;
  const [errorDescription, setErrorDescription] = useState<string>("");
  const [deviceMenuItem, setDeviceMenuItem] = useState<string>("");
  const [deviceMenuItems, setDeviceMenuItems] = useState<
    {
      value: string;
      name: string;
    }[]
  >();
  const [{ issueType, issueTypes }, dispatch] = React.useReducer(
    (state: IssueTypeState, action: IssueTypeState) => ({
      ...state,
      ...action,
    }),
    {
      issueType: "",
      issueTypes: [],
    }
  );

  const disabled = ticket.status === "Closed" || !canEdit;

  const fetchIssues = async (accountId: string) => {
    if (accountId) {
      const issueTypes = await getIssue(accountId, "type" as IssueName);
      if (ticket.issue?.issue) {
        if (!issueTypes.find((issue) => issue === ticket.issue?.issue)) {
          issueTypes.push(ticket.issue?.issue ?? "");
        }
      }
      dispatch({
        issueTypes,
        issueType:
          ticket.issue?.issue !== undefined && ticket.issue?.issue !== ""
            ? ticket.issue?.issue
            : issueTypes[0],
      });
    }
  };

  useEffect(() => {
    buildDevicePicker();
    if (ticket.account) {
      fetchIssues(ticket?.account?.id);
    }
  }, [ticket.account]);

  useEffect(() => {
    if (isNewTicket) {
      dispatchTicket({
        issue: { ...ticket.issue, issue: issueTypes[0] },
        subject: getSubject(
          ticket,
          issueTypes[0],
          meeting,
          ticket.device?.id,
          meeting?.meetingId?.toString()
        ),
      });
    }
  }, [isNewTicket, issueTypes]);

  const handleIssueChange = (value: string, field: keyof TicketIssue) => {
    if (field == "type") {
      dispatch({
        issueTypes,
        issueType: value,
      });
      dispatchTicket({
        issue: { ...ticket.issue, issue: value },
        subject: isNewTicket
          ? getSubject(
              ticket,
              value,
              meeting,
              ticket.device?.id,
              meeting?.meetingId?.toString()
            )
          : ticket.subject,
      });
    }
  };

  const handleDateChanges = (date: number | undefined) => {
    if (date !== undefined) dispatchTimeOfIssue({ date: date });
  };

  const getFullProductName = (device: Device) => {
    let productName = getProductName(device);
    productName += device.room ? ` - ${device.room.name}` : "";
    productName += device.room?.location
      ? ` - ${device.room.location.name}`
      : "";
    return productName;
  };
  const buildDevicePicker = () => {
    let tmp = [];

    if (
      meeting?.id ||
      (ticket.meetings !== undefined && ticket.meetings.length > 0)
    ) {
      tmp.push({ value: "all", name: t("All (Meeting Level Ticket)") });
    } else if (ticket && ticket.room && ticket.id && !ticket.device) {
      tmp.push({
        value: "room",
        name: t("All (Room Level Ticket)"),
      });
    } else if (
      ticket.id &&
      ticket.room === undefined &&
      ticket.device === undefined &&
      ticket.account !== undefined
    ) {
      tmp.push({
        value: "account",
        name: t("All (Account Level Ticket)"),
      });
    }
    if (device) {
      tmp.push({ value: device.id ?? "", name: getFullProductName(device) });
    } else if (ticket && ticket.device) {
      tmp.push({
        value: ticket.device.id ?? "",
        name: getFullProductName(ticket.device),
      });
    } else if (devices) {
      devices.forEach((device) => {
        tmp.push({
          value: device.id ?? "",
          name: getFullProductName(device),
        });
      });
    } else if (meeting?.participants) {
      meeting.participants.forEach((participant) => {
        if (participant.device)
          tmp.push({
            value: participant.device.id ?? "",
            name: getFullProductName(participant.device),
          });
      });
    }

    setDeviceMenuItems(tmp);
    if (tmp.length > 0) {
      if (ticket.device) setDeviceMenuItem(ticket.device.id ?? "");
      else if (devices && devices.length === 1)
        handleDeviceChange(devices[0].id ?? "");
      else if (
        meeting?.id ||
        (ticket.meetings !== undefined && ticket.meetings.length > 0)
      )
        setDeviceMenuItem("all");
      else if (ticket.room && ticket.id) setDeviceMenuItem("room");
      else if (
        ticket.id &&
        ticket.room === undefined &&
        ticket.device === undefined &&
        ticket.account !== undefined
      )
        setDeviceMenuItem("account");
    }
  };

  const handleDeviceChange = (deviceId: string): void => {
    setDeviceMenuItem(deviceId);
    if (deviceId == "room") {
      delete ticket["device"];
    } else if (deviceId == "all") {
      if (
        meeting?.id ||
        (ticket.meetings !== undefined && ticket.meetings.length > 0)
      ) {
        delete ticket["device"];
        delete ticket["room"];
      } else if (ticket.room && ticket.id) {
        delete ticket["device"];
      }
    } else if (deviceId == "account") {
      delete ticket["device"];
      delete ticket["room"];
    } else if (deviceId != "") {
      if (ticket?.device?.id != deviceId) {
        dispatchTicket({ device: { id: deviceId } });
      }
    }
  };

  const priorityList = () => {
    return [
      { value: "Critical", title: t("P1 - Critical") },
      { value: "Major", title: t("P2 - Major") },
      { value: "Minor", title: t("P3 - Minor") },
      { value: "Informational", title: t("P4 - Informational") },
    ].map((key) => {
      return (
        <MenuItem key={key.value} value={key.value}>
          {key.title}
        </MenuItem>
      );
    });
  };

  const timeList = () => {
    return [
      {
        value: TimeValue.FewSeconds,
        title: t(TimeValueLabels[TimeValue.FewSeconds]),
      },
      { value: TimeValue.NoClue, title: t(TimeValueLabels[TimeValue.NoClue]) },
      {
        value: TimeValue.EnterDate,
        title: t(TimeValueLabels[TimeValue.EnterDate]),
      },
    ].map((key) => {
      return (
        <MenuItem key={key.value} value={key.value}>
          {key.title}
        </MenuItem>
      );
    });
  };

  const ref = useRef<HTMLDivElement>(null);

  return (
    <Grid
      container
      sx={(theme) => ({
        flexDirection: "row",
        padding: theme.spacing(2, 4),
        width: "100%",
      })}
    >
      <Grid
        sx={{
          flex: "1",
          width: "90%",
        }}>
        <Grid
          container
          spacing={2}
          wrap="nowrap"
          sx={{ width: '100%', flex: 1,
            flexDirection: "row", }}
        >
          <Grid
            sx={{
              width: "50%",
            }}>
            <Grid
              container
              spacing={2}
              sx={{
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Grid
                sx={{
                  width: "100%",
                }}>
                <FormControl fullWidth size="small">
                  <Select
                    required
                    labelId="inputDeviceLabel"
                    id="inputDevice"
                    value={deviceMenuItem ?? ""}
                    label={t("Device")}
                    onChange={(e) => {
                      handleDeviceChange(e.target.value as string);
                      dispatchHasChanged(true);
                    }}
                    disabled={disabled}
                  >
                    {deviceMenuItems?.map((item) => {
                      return (
                        <MenuItem
                          key={item.value}
                          value={item.value}
                          sx={{
                            "& > span": {
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              display: "inline-block",
                              width: "100%",
                            },
                          }}
                        >
                          <Tooltip
                            title={item.name ?? ""}
                            arrow
                            slotProps={{
                              popper: {
                                popperOptions: {
                                  modifiers: [
                                    {
                                      name: "preventOverflow",
                                      options: {
                                        boundary: ref?.current,
                                      },
                                    },
                                  ],
                                },
                              }
                            }}
                          >
                            <Typography
                              fontSize={"1rem"}
                              noWrap
                              width={
                                ref.current
                                  ? ref.current?.getBoundingClientRect().width
                                  : "auto"
                              }
                            >
                              {item.name}
                            </Typography>
                          </Tooltip>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                sx={{
                  width: "100%",
                }}>
                <FormControl fullWidth size="small">
                  <Select
                    required
                    labelId="inputIssueTypeLabel"
                    id="inputIssueType"
                    value={issueType ?? ""}
                    label={t("Issue Type")}
                    onChange={(e) => {
                      handleIssueChange(e.target.value as string, "type");
                      dispatchHasChanged(true);
                    }}
                    disabled={disabled}
                  >
                    {issueTypes?.map((type) => {
                      return (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <SubTypePicker device={device}></SubTypePicker>
              </Grid>
              <Grid>
                <FormControl fullWidth size="small">
                  <Select
                    required
                    labelId="inputPriorityLabel"
                    id="inputPriority"
                    value={ticket.priority ?? ["Critical"]}
                    label={t("Priority")}
                    disabled={disabled}
                    onChange={(evt) => {
                      dispatchTicket({
                        priority: evt.target.value as Priority,
                      });
                      dispatchHasChanged(true);
                    }}
                  >
                    {priorityList()}
                  </Select>
                </FormControl>
              </Grid>
              {isNewTicket && (
                <Grid>
                  <FormControl fullWidth size="small">
                    <Select
                      required
                      labelId="inputTimeLabel"
                      id="inputTime"
                      value={timeOfIssue.type}
                      label={t("Time of Issue")}
                      disabled={disabled}
                      onChange={(evt) => {
                        dispatchTimeOfIssue({
                          type: evt.target.value as TimeValue,
                          date: new Date().getTime(),
                        });
                        dispatchHasChanged(true);
                      }}
                    >
                      {timeList()}
                    </Select>
                  </FormControl>
                  {timeOfIssue.type === TimeValue.EnterDate && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        maxDate={new Date()}
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            fullWidth: true,
                            required: true,
                          },
                        }}
                        onChange={(date: Date | null) => {
                          handleDateChanges(date?.getTime() ?? undefined);
                          dispatchHasChanged(true);
                        }}
                        sx={{ pt: 2 }}
                        value={new Date(timeOfIssue.date) ?? undefined}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid
            sx={{
              flex: "1",
            }}>
            <FormControl
              fullWidth
              size="small"
              sx={(theme) => ({
                width: "100%",
                "& .MuiFormHelperText-root": {
                  color: theme.palette.error.main,
                },
              })}
            >
              <TextField
                sx={(theme) => ({
                  "&.MuiFormControl-root": {
                    height: "100%",
                    "& .MuiInputBase-root": {
                      height: "100%",
                    },
                  },
                })}
                label={t("Description")}
                value={details.description ?? ""}
                required
                rows="8"
                multiline
                fullWidth
                disabled={disabled}
                onChange={(evt) => {
                  if (evt.target.value === "")
                    setErrorDescription("Field is required");
                  else if (evt.target.value.length >= 2000)
                    setErrorDescription(
                      "Please enter a shorter description (2000 characters limit)."
                    );
                  else setErrorDescription("");
                  dispatchDetails({ description: evt.target.value });
                  dispatchHasChanged(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 2000 }
                }}
              />
              <FormHelperText>{errorDescription}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      <Divider orientation="vertical" flexItem></Divider>
      <Grid
        size={{
          md: 1
        }}>
        <Grid
          container
          sx={{ width: '100%', flexDirection: "column", }}
        >
          <Grid>
            <StyledTypo size="small">{t("Events")}</StyledTypo>
            <Typography size="big" realTime>
              {ticket.eventCount}
            </Typography>
          </Grid>
          <Grid>
            <StyledTypo size="small">{t("Affected Conf.")}</StyledTypo>
            <Typography size="big" realTime>
              {ticket.meetings && ticket.meetings.length !== 0
                ? ticket.meetings.length
                : 0}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TicketDetails;
